import mixpanel from "mixpanel-browser";
import { Analytics, EcommerceEvent, TrackProperties } from "./types";

export class MixpanelSourceProvider {
  initialize(client: MixpanelClient) {
    mixpanel.init(client.token, {
      debug: !!client.debug,
      record_sessions_percent: 1,
    });
    return MixpanelAnalytics();
  }
}

interface MixpanelTrackerOptions {
  token: string;
  debug?: boolean;
  sendPageView?: boolean;
}

export interface MixpanelClient extends MixpanelTrackerOptions {
  provider: "mixpanel";
}

export function makeMixpanelClient(
  tracker: MixpanelTrackerOptions
): MixpanelClient {
  return {
    provider: "mixpanel",
    token: tracker.token,
    debug: tracker.debug,
    sendPageView: tracker.sendPageView ?? true,
  };
}

function MixpanelAnalytics(): Analytics {
  return {
    group: () => {},
    register: (props) => {
      mixpanel.register(props);
    },
    identify: (userId, traits) => {
      if (!traits) {
        return;
      }

      if (!traits.email) {
        return;
      }

      mixpanel.identify(traits.email);
      mixpanel.people.set({
        $name: traits.name,
        $email: traits.email,
        "Company Name": traits.company?.name,
        "Created At": traits.createdAt?.toLocaleDateString("en-GB"),
        "Phone Number": traits.phone,
        "User ID": userId,
      });
    },
    screen: (name, properties?) => {
      if (!name || !properties) {
        return;
      }

      const {
        partnerId,
        partnerName,
        partnerInternalName,
        pageName,
        template,
        catalogSlug,
        ...mainProperties
      } = properties;

      // only track non empty value to avoid undefined, null, ''
      if (!partnerId || !partnerName || !pageName) {
        return;
      }

      // super properties that will be sent to mixpanel with every sub-sequence requests
      mixpanel.register({
        "Partner UUID": partnerId,
        "Partner Name": partnerName,
        "Partner Internal Name": partnerInternalName,
        "Page Name": pageName,
        "Page URL": window.location.href,
        ...(catalogSlug && { "Catalog Slug": catalogSlug }),
      });

      if (template) {
        // track template as super property if available
        mixpanel.register({
          Template: template,
        });
      } else {
        // in page that does not have template, unset it from super property
        mixpanel.register({
          Template: null,
        });
      }

      mixpanel.track(name, mainProperties);
    },
    ecommerceEvent: (event: EcommerceEvent) => {
      switch (event.event) {
        case "Product Viewed":
          mixpanel.track("Product Viewed", {
            "Purchased Products": event.products,
            "Order total": event.revenue,
            "Order total currency": event.currency,
          });
          break;
        case "Checkout Started":
          mixpanel.track("Checkout Started", {
            "Purchased Products": event.products,
            "Order total": event.revenue,
            "Order total currency": event.currency,
          });
          break;
        case "Promotion Viewed":
          mixpanel.track("Promotion Viewed", {
            "Promotion ID": event.id,
            "Promotion Name": event.name,
            "Promotion Items": event.items,
          });
          break;
        case "Promotion Clicked":
          mixpanel.track("Promotion Clicked", {
            "Promotion ID": event.id,
            "Promotion Name": event.name,
            "Promotion Items": event.items,
          });
          break;
        case "Order Completed":
          mixpanel.track("Order Purchase", {
            $name: event.name,
            $email: event.email,
            "Company Name": event.company?.name,
            "Created At": event.createdAt?.toLocaleDateString("en-GB"),
            "Phone Number": event.phone,
            "Purchased Products": event.products,
            "Page URL": window.location.href,
            Template: event.template,
            "Order total": event.revenue,
            "Order total in USD": event.revenueInUsd,
            "Order total currency": event.currency,
            "Order rate type": event.bookingType,
            "Start Date": event.startDate,
            "End Date": event.endDate,
            "Start Time": event.startTime,
            "End Time": event.endTime,
            Duration: event.duration,
          });
          break;
        default:
          console.error(`Analytics: event ${event.event} not supported yet.`);
          break;
      }
    },
    event: (eventName: string, properties?: TrackProperties) => {
      mixpanel.track(eventName, properties);
    },
  };
}
